/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Card, CardHeader, Avatar, Grid, Typography, Tooltip } from '@material-ui/core';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { withStyles, useTheme } from '@material-ui/core/styles';
import { BarChart } from '@material-ui/icons';
import moment from 'moment';
import 'moment-timezone';
import TimeAgo from 'react-timeago';
import { usePricing } from '../../../context/pricing-context';
import { forceRefresh } from '../../../services/killshot';

const MetaPanelGridWrap = styled(Grid)`
  align-items: center;
`;

const styles = () => ({
  paperSectionGeneric: {
    marginTop: '8px',
  },
  cardHeaderSection: {
    background: '#F4F4F4',
  },
  cardHeaderSectionDark: {
    background: '#2D3436',
  },
  cardContent: {
    background: 'white',
  },
  cardContentDark: {
    background: '#262C2E',
  },
});

const CircleIcon = styled.div`
  width: 19px;
  height: 20px;
  border-radius: 50%;
  background: #9c27b0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const useStyles = makeStyles({
  eventStatsLabel: {
    fontSize: '16px',
  },
  eventStatsMetrics: {
    fontSize: '14px',
    paddingTop: '5px',
  },
});

function MetaPanel(props) {
  const [state, setState] = useState({
    isLoading: false,
  });
  const { setRefresh, marketplace } = props;

  const { fetchData, skyboxId } = usePricing();

  const refreshData = async () => {
    setState({ ...state, isLoading: true });
    forceRefresh(skyboxId, marketplace);
    await fetchData();
    setRefresh();
    setState({ ...state, isLoading: false });
  };
  const theme = useTheme();

  const { classes, eventMeta, SBEventMeta } = props;
  const updatedTimes = [
    moment
      .utc(SBEventMeta?.last_updated?.skybox)
      .local()
      .format('YYYY-MM-DD HH:mm:ss'),
  ];

  const groupClasses = useStyles();

  return (
    <Card square className={classes.paperSectionGeneric}>
      <CardHeader
        className={theme.palette.type === 'light' ? classes.cardHeaderSection : classes.cardHeaderSectionDark}
        titleTypographyProps={{ style: { fontSize: 20 } }}
        style={{ padding: '20px 15px' }}
        // prettier-ignore
        avatar={(
          <Avatar aria-label="Event Stats" style={{background:"#7D1F8D"}}>
            <BarChart style={{color:"white"}} />
          </Avatar>
        )}
        // prettier-ignore
        action={(
          <MetaPanelGridWrap container style={{paddingTop:"16px", paddingRight:"11px"}} justifyContent='space-between' alignItems='flex-start' spacing={2}>
            <Grid item>
              <Typography style={{fontSize: 16}}>Last Updated</Typography>
            </Grid>
            <Grid item>
              <Typography style={{fontSize: 14}}>
                {SBEventMeta?.last_updated?.skybox && <TimeAgo date={updatedTimes[0]} />}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title='Refresh Data' arrow placement='top' onClick={refreshData}>
                <CircleIcon>
                  <AutorenewIcon style={{ color: 'white', fontSize: 'small' }} />
                </CircleIcon>
              </Tooltip>
            </Grid>
          </MetaPanelGridWrap>
        )}
        title="POS Event Stats"
      />
      <Grid
        container
        className={theme.palette.type === 'light' ? classes.cardContent : classes.cardContentDark}
        direction="row"
        justifyContent="space-between"
        style={{ padding: '20px 50px' }}
      >
        <Grid item>
          <Grid container direction="column" spacing={12}>
            <Grid item>
              <Typography className={groupClasses.eventStatsLabel}>Purchased</Typography>
            </Grid>
            <Grid item>
              <Typography className={groupClasses.eventStatsMetrics}>
                {eventMeta.sb_stats.quantity_purchased}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <Typography className={groupClasses.eventStatsLabel}>Held</Typography>
            </Grid>
            <Grid item>
              <Typography className={groupClasses.eventStatsMetrics}>{eventMeta.sb_stats.quantity_held}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <Typography className={groupClasses.eventStatsLabel}>Sold</Typography>
            </Grid>
            <Grid item>
              <Typography className={groupClasses.eventStatsMetrics}>{eventMeta.sb_stats.quantity_sold}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <Typography className={groupClasses.eventStatsLabel}>1D&nbsp;Pace</Typography>
            </Grid>
            <Grid item>
              <Typography className={groupClasses.eventStatsMetrics}>{eventMeta.sb_stats.one_day_pace}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <Typography className={groupClasses.eventStatsLabel}>3D&nbsp;Pace</Typography>
            </Grid>
            <Grid item>
              <Typography className={groupClasses.eventStatsMetrics}>{eventMeta.sb_stats.three_day_pace}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <Typography className={groupClasses.eventStatsLabel}>7D&nbsp;Pace</Typography>
            </Grid>
            <Grid item>
              <Typography className={groupClasses.eventStatsMetrics}>{eventMeta.sb_stats.seven_day_pace}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Grid container direction="column">
            <Grid item>
              <Typography className={groupClasses.eventStatsLabel}>30D&nbsp;Pace</Typography>
            </Grid>
            <Grid item>
              <Typography className={groupClasses.eventStatsMetrics}>{eventMeta.sb_stats.thirty_day_pace}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
}

MetaPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  eventMeta: PropTypes.object.isRequired,
  SBEventMeta: PropTypes.object.isRequired,
  setRefresh: PropTypes.func.isRequired,
  marketplace: PropTypes.object.isRequired,
};

export default withStyles(styles)(MetaPanel);
